export const CANONICAL_VISIBILITY = Object.freeze({
    PUBLIC: 'PUBLIC',
    FRIENDS: 'FRIENDS',
    OWNER_ONLY: 'OWNER_ONLY',
    PRIVATE: 'PRIVATE',
});

export const CANONICAL_VISIBILITY_VALUES = Object.freeze(Object.values(CANONICAL_VISIBILITY));
export const CANONICAL_VISIBILITY_VALUE_SET = new Set(CANONICAL_VISIBILITY_VALUES);

export const DISCOVER_PRIVACY = Object.freeze({
    PUBLIC: 'PUBLIC',
    FRIENDS: 'FRIENDS',
    ONLY_ME: 'ONLY_ME',
    PRIVATE: 'PRIVATE',
});

export const DISCOVER_PRIVACY_VALUES = Object.freeze(Object.values(DISCOVER_PRIVACY));
export const DISCOVER_PRIVACY_VALUE_SET = new Set(DISCOVER_PRIVACY_VALUES);

export const POST_API_VISIBILITY = Object.freeze({
    PUBLIC: 'VISIBILITY_PUBLIC',
    FRIENDS: 'VISIBILITY_FRIENDS',
    OWNER_ONLY: 'VISIBILITY_ONLY_ME',
    PRIVATE: 'VISIBILITY_PRIVATE',
});

export const CLOSED_VISIBILITY_FALLBACK = CANONICAL_VISIBILITY.PRIVATE;

export const normalizeVisibilityToken = (value) => {
    if (typeof value !== 'string') {
        return '';
    }
    return value.trim().toUpperCase().replace(/[\s-]+/g, '_');
};

const VISIBILITY_ALIASES = Object.freeze({
    PUBLIC: CANONICAL_VISIBILITY.PUBLIC,
    FRIENDS: CANONICAL_VISIBILITY.FRIENDS,
    FOLLOWERS: CANONICAL_VISIBILITY.FRIENDS,
    ONLY_ME: CANONICAL_VISIBILITY.OWNER_ONLY,
    OWNER_ONLY: CANONICAL_VISIBILITY.OWNER_ONLY,
    PRIVATE: CANONICAL_VISIBILITY.PRIVATE,
    VISIBILITY_PUBLIC: CANONICAL_VISIBILITY.PUBLIC,
    VISIBILITY_FRIENDS: CANONICAL_VISIBILITY.FRIENDS,
    VISIBILITY_ONLY_ME: CANONICAL_VISIBILITY.OWNER_ONLY,
    VISIBILITY_PRIVATE: CANONICAL_VISIBILITY.PRIVATE,
});

export function parseCanonicalVisibility(value) {
    const normalized = normalizeVisibilityToken(value);
    return VISIBILITY_ALIASES[normalized] || null;
}

export function normalizeCanonicalVisibility(value, fallback = CLOSED_VISIBILITY_FALLBACK) {
    return parseCanonicalVisibility(value) || parseCanonicalVisibility(fallback) || CLOSED_VISIBILITY_FALLBACK;
}

export function canonicalVisibilityToDiscoverPrivacy(value, fallback = DISCOVER_PRIVACY.PRIVATE) {
    const canonical = parseCanonicalVisibility(value);
    if (!canonical) {
        return normalizeDiscoverPrivacyFallback(fallback);
    }

    switch (canonical) {
        case CANONICAL_VISIBILITY.PUBLIC:
            return DISCOVER_PRIVACY.PUBLIC;
        case CANONICAL_VISIBILITY.FRIENDS:
            return DISCOVER_PRIVACY.FRIENDS;
        case CANONICAL_VISIBILITY.OWNER_ONLY:
            return DISCOVER_PRIVACY.ONLY_ME;
        case CANONICAL_VISIBILITY.PRIVATE:
        default:
            return DISCOVER_PRIVACY.PRIVATE;
    }
}

export function discoverPrivacyToCanonicalVisibility(value, fallback = CLOSED_VISIBILITY_FALLBACK) {
    const normalized = normalizeVisibilityToken(value);

    switch (normalized) {
        case DISCOVER_PRIVACY.PUBLIC:
            return CANONICAL_VISIBILITY.PUBLIC;
        case DISCOVER_PRIVACY.FRIENDS:
            return CANONICAL_VISIBILITY.FRIENDS;
        case DISCOVER_PRIVACY.ONLY_ME:
            return CANONICAL_VISIBILITY.OWNER_ONLY;
        case DISCOVER_PRIVACY.PRIVATE:
            return CANONICAL_VISIBILITY.PRIVATE;
        default:
            return normalizeCanonicalVisibility(value, fallback);
    }
}

export function normalizeDiscoverPrivacyFallback(value) {
    const normalized = normalizeVisibilityToken(value);
    if (DISCOVER_PRIVACY_VALUE_SET.has(normalized)) {
        return normalized;
    }
    return canonicalVisibilityToDiscoverPrivacy(value, DISCOVER_PRIVACY.PRIVATE);
}

export function normalizeDiscoverPrivacyValue(value, fallback = DISCOVER_PRIVACY.PRIVATE) {
    const normalized = normalizeVisibilityToken(value);
    if (DISCOVER_PRIVACY_VALUE_SET.has(normalized)) {
        return normalized;
    }
    const canonical = parseCanonicalVisibility(value);
    if (canonical) {
        return canonicalVisibilityToDiscoverPrivacy(canonical, fallback);
    }
    return normalizeDiscoverPrivacyFallback(fallback);
}

export function canonicalVisibilityToPostApiVisibility(value) {
    const canonical = parseCanonicalVisibility(value);
    switch (canonical) {
        case CANONICAL_VISIBILITY.PUBLIC:
            return POST_API_VISIBILITY.PUBLIC;
        case CANONICAL_VISIBILITY.FRIENDS:
            return POST_API_VISIBILITY.FRIENDS;
        case CANONICAL_VISIBILITY.OWNER_ONLY:
            return POST_API_VISIBILITY.OWNER_ONLY;
        case CANONICAL_VISIBILITY.PRIVATE:
            return POST_API_VISIBILITY.PRIVATE;
        default:
            return null;
    }
}
